import { Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { BLOG_POSTS } from '../content/blog/registry';
import { useBlogPostBySlug } from '../hooks/useBlogPosts';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { BlogPostLegacy } from './BlogPostLegacy';

/**
 * Blog post dispatcher.
 *
 * Resolution order for `/blog/:slug`:
 *   1. Slug matches the LEGACY test article → render BlogPostLegacy (no DB check)
 *   2. Slug exists in the auto-generated registry → check Firestore status:
 *        - status === 'published' → render the post's lazy component
 *        - anything else (including not-found / permission-denied) → 404
 *   3. Otherwise → 404
 */

const LEGACY_SLUG = 'how-to-publish-a-book-on-amazon-kindle-2026';

function PostNotFound() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar variant="public" />
      <main className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="text-center">
          <div className="text-5xl mb-4" aria-hidden="true">📚</div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Post not found</h1>
          <p className="text-muted-foreground">This article may have been moved or unpublished.</p>
          <a
            href="/blog"
            className="inline-block mt-6 text-sm font-semibold text-[#a78bfa] hover:text-foreground transition-colors"
          >
            ← Back to all posts
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar variant="public" />
      <main className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </main>
      <Footer />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();

  // 1. Legacy short-circuit — render the original hardcoded test article.
  if (slug === LEGACY_SLUG) {
    return <BlogPostLegacy />;
  }

  const entry = slug ? BLOG_POSTS[slug] : undefined;
  const { data: post, isLoading } = useBlogPostBySlug(slug);

  // 3. Unknown slug
  if (!entry) {
    return <PostNotFound />;
  }

  // While we wait for the Firestore status check
  if (isLoading) {
    return <PostSkeleton />;
  }

  // 2. Status check — anything not 'published' (or null from permission denied) is 404
  if (!post || post.status !== 'published') {
    return <PostNotFound />;
  }

  // 2a. Render the dynamic post
  const { Component } = entry;
  return (
    <Suspense fallback={<PostSkeleton />}>
      <Component />
    </Suspense>
  );
}
